/**
 * WARP-567 — the full-screen splash shown while switching in or out of "View as".
 *
 * Swapping identity reloads the page, so the app is torn down and rebuilt and nothing
 * React renders can cover the gap. The splash markup therefore lives in index.html and
 * is driven from three places: here (before the reload), an inline script in index.html
 * (while the new document boots), and main.jsx (dismissed once React has painted).
 *
 * The message is stashed in sessionStorage so it survives the reload. Storage can throw
 * outright in private mode, so every access is guarded — losing the splash is a cosmetic
 * problem, and must never break the identity swap it accompanies.
 */

const STORAGE_KEY = 'warp:identity-splash'
const VISIBLE_CLASS = 'identity-switching'

/**
 * How long the splash stays up, measured from the click rather than from boot. Switching
 * identity is rare, deliberate, and changes whose data is on screen, so it is worth a
 * beat to read who you are becoming. Measuring from the click means a slow request or a
 * slow boot counts toward it and nothing is added on top.
 */
const MIN_VISIBLE_MS = 1500
const FADE_MS = 180

const element = () => document.getElementById('identity-splash')

const readStash = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY)) || null
  } catch {
    return null
  }
}

const forget = () => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}

const hideNow = () => {
  document.documentElement?.classList.remove(VISIBLE_CLASS)
  element()?.removeAttribute('data-leaving')
}

const wait = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

/** Resolve after the browser has actually painted, not merely after React returns. */
const afterNextPaint = () =>
  new Promise((resolve) => {
    if (typeof window.requestAnimationFrame !== 'function') {
      resolve()
      return
    }
    window.requestAnimationFrame(() => window.requestAnimationFrame(resolve))
  })

/**
 * Show the splash now and keep it up across the reload that follows.
 *
 * @param {string} message - The headline, e.g. "Switching to Jane Doe".
 * @param {string} detail - The supporting line beneath it.
 */
export const armIdentitySplash = (message, detail = '') => {
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ message, detail, armedAt: Date.now() }),
    )
  } catch {
    // Storage unavailable — the splash simply will not survive the reload.
  }

  const messageNode = document.getElementById('identity-splash-message')
  const detailNode = document.getElementById('identity-splash-detail')
  if (messageNode) messageNode.textContent = message
  if (detailNode) detailNode.textContent = detail

  document.documentElement?.classList.add(VISIBLE_CLASS)
}

/**
 * Take the splash down once the app is on screen and it has been readable long enough.
 *
 * Waits for a real paint before fading: React 18 renders concurrently, so returning from
 * `render()` does not mean the app is visible, and hiding at that point re-opens the
 * blank gap the splash exists to cover.
 */
export const dismissIdentitySplash = async () => {
  const stash = readStash()
  forget()

  if (!stash) {
    hideNow()
    return
  }

  const shownFor = Date.now() - (stash.armedAt || 0)
  const remaining = Math.max(0, MIN_VISIBLE_MS - shownFor)

  await afterNextPaint()
  if (remaining) await wait(remaining)

  element()?.setAttribute('data-leaving', 'true')
  await wait(FADE_MS)
  hideNow()
}

/**
 * Hide the splash immediately and disarm it. Used when the swap fails and no reload is
 * coming, so there is nothing to wait for.
 */
export const clearIdentitySplash = () => {
  forget()
  hideNow()
}
